// components
import Image from 'next/image';
import Markdown from 'react-markdown';
import VisibleLoading from '@/components/utilities/VisibleLoading';
import YoutubeVideo from '@/components/utilities/YoutubeVideo';

// utilities
import { getImagePath } from '@/utils/images';

const ColumnsImageWithTextBlock = ({
  columnsImage: image,
  title,
  subtitle,
  text,
  variant,
  background,
  backgroundVariant,
  isVideo,
  video,
  shadow,
  borderRadius
}) => {
  return (
    <div
      className={`relative mx-auto flex w-[90vw] flex-col items-center gap-9 lg:gap-32 xl:w-(--container-width) ${variant === 'right' ? 'lg:flex-row-reverse' : 'lg:flex-row'} ${background ? `overflow-hidden rounded-3xl ${backgroundVariant === 'dark' ? 'border border-white/20 bg-white/3' : 'bg-white'} py-6 shadow-2xl/15 ${variant === 'right' ? 'px-6 xl:pl-12' : 'px-6 xl:pr-12'}` : 'justify-center py-16'}`.trim()}
    >
      {background && backgroundVariant === 'dark' && (
        <>
          <svg className="absolute h-full w-full" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="lines" patternUnits="userSpaceOnUse" width="32" height="32">
                <line x1="0" y1="0" x2="32" y2="0" className="stroke-white/20" strokeWidth="0.25" />
                <line x1="0" y1="0" x2="0" y2="32" className="stroke-white/20" strokeWidth="0.25" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#lines)" />
          </svg>

          <VisibleLoading delay={500}>
            {({ ref, className }) => (
              <div
                ref={ref}
                className={`gpu-render bg-white-50 absolute -top-[150px] right-1/2 h-[200px] w-[400px] blur-[200px] lg:block ${className}`}
              />
            )}
          </VisibleLoading>

          <VisibleLoading delay={600}>
            {({ ref, className }) => (
              <div
                ref={ref}
                className={`gpu-render bg-white-20 absolute -right-[200px] -bottom-[100px] h-[200px] w-[400px] blur-[140px] lg:block ${className}`}
              />
            )}
          </VisibleLoading>
        </>
      )}

      <VisibleLoading delay={700} direction={variant === 'right' ? 'right' : 'left'}>
        {({ ref, className }) => (
          <div
            ref={ref}
            className={`w-[335px] overflow-hidden ${borderRadius ? 'rounded-2xl' : ''} lg:w-[488px] ${shadow ? 'shadow-2xl/15' : ''} ${className || ''}`.trim()}
          >
            {isVideo && video?.videoId ? (
              <div className={`h-[200px] w-full xl:h-[370px]`}>
                <YoutubeVideo
                  videoId={video.videoId}
                  thumbnail={
                    video.image ? (
                      <Image
                        src={getImagePath(video.image)}
                        alt={video.image.alternativeText || title}
                        fill={true}
                        className="object-cover"
                      />
                    ) : null
                  }
                />
              </div>
            ) : image ? (
              <Image
                src={getImagePath(image)}
                width={image.width}
                height={image.height}
                alt={image.alternativeText || title}
                sizes="(min-width: 1024px) 488px, 90vw"
                className="mx-auto"
              />
            ) : null}
          </div>
        )}
      </VisibleLoading>

      <VisibleLoading delay={100} direction={variant !== 'right' ? 'right' : 'left'}>
        {({ ref, className }) => (
          <div ref={ref} className={` ${className || ''} ${background ? `flex-1` : ''}`.trim()}>
            <div className="txt-md text-grey-600 mb-4 max-w-[335px] text-left font-medium uppercase lg:max-w-[488px]">
              {subtitle}
            </div>
            <h2 className="max-w-[335px] text-left lg:max-w-[488px]">{title}</h2>
            <div className="txt-xl text-grey-700 mt-6 max-w-[335px] text-left whitespace-pre-wrap lg:max-w-[488px] [&_a]:underline">
              <Markdown
                components={{
                  a: ({ href, children }) => {
                    const isExternal = href && !href.startsWith('/') && !href.startsWith('#');
                    return (
                      <a href={href} {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                        {children}
                      </a>
                    );
                  }
                }}
              >
                {text}
              </Markdown>
            </div>
          </div>
        )}
      </VisibleLoading>
    </div>
  );
};
export default ColumnsImageWithTextBlock;
